import { Injectable } from '@angular/core';
import L from 'leaflet';
import * as moment from 'moment';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/observable/of';

import { Stop, LeafletStop, ScheduledStop, StopSelectionType } from '../core/stop';
import { TramApiService } from '../tram-api.service';
import { Route } from '../core/route';
import { ScheduleCollector } from './schedule-collector';
import { LeafletTram, Tram } from '../core/tram';
import { LiteEvent } from './index';

@Injectable()
export class StopCollector {
    private _stops: Map<number, LeafletStop>;
    get stops(): LeafletStop[] {
        let stopsInternal: LeafletStop[] = [];

        if (this._stops !== undefined)
            this._stops.forEach(s => stopsInternal.push(s));

        return stopsInternal;
    }

    private readonly _onInitialized = new LiteEvent<LeafletStop[]>();
    private readonly _onBeforeSelect = new LiteEvent<LeafletStop>();
    private readonly _onAfterSelect = new LiteEvent<LeafletStop>();
    private readonly _onBeforeUnselect = new LiteEvent<LeafletStop>();
    private readonly _onAfterUnselect = new LiteEvent<LeafletStop>();

    get afterInitialize() { return this._onInitialized.expose(); };
    onStopInitialized: (stop: LeafletStop) => void;
    get beforeSelect() { return this._onBeforeSelect.expose(); }
    get afterSelect() { return this._onAfterSelect.expose(); }
    get beforeUnselect() { return this._onBeforeUnselect.expose(); }
    get afterUnselect() { return this._onAfterUnselect.expose(); }

    constructor(
        private api: TramApiService,
        private scheduleCollector: ScheduleCollector) {
        this._stops = new Map<number, LeafletStop>();
    }

    init(canSelect: boolean) {
        this._stops.clear();

        return this.api.getStops(0, 0)
            .then(stops => {
                stops.forEach(s => {
                    let lStop = this.convertInternal(s, canSelect);
                    this._stops.set(s.id, lStop);

                    if (this.onStopInitialized)
                        this.onStopInitialized(lStop);
                });

                if (this._onInitialized) {
                    this._onInitialized.trigger(this.stops);
                }

                return this.stops;
            });
    }

    initByRoutes(routes: Route[]) {
        this._stops.clear();

        return this.api.getStops(0, 0)
            .then(stops => {
                let internalStops: Map<number, Stop> = new Map<number, Stop>();
                stops.forEach(s => internalStops.set(s.id, s));

                let allStopIds: number[] = [];
                routes.forEach(r => { allStopIds = allStopIds.concat(r.allStopIds); });
                allStopIds = Array.from(new Set(allStopIds));

                allStopIds.forEach(id => {
                    if (!internalStops.has(id))
                        return;

                    let stop = internalStops.get(id);

                    let lStop = this.convertInternal(stop, true);
                    this._stops.set(stop.id, lStop);

                    if (this.onStopInitialized)
                        this.onStopInitialized(lStop);
                });

                if (this._onInitialized) {
                    this._onInitialized.trigger(this.stops);
                }

                return this.stops;
            });
    }

    get(id: number): Stop;
    get(route: Route, isReverse: boolean): Stop[];
    get(...objects: any[]): any {
        if (typeof (objects[0]) === 'number') {
            if (!this._stops.has(objects[0]))
                return null;
            return this._stops.get(objects[0]);
        }

        let route = objects[0];
        let isReverse = objects[1];

        let stops: LeafletStop[] = [];
        let ids = isReverse
            ? route.reverseStopIds
            : route.stopIds;

        ids.forEach(id => {
            if (!this._stops.has(id)) return;

            const stop = this._stops.get(id)
            stops.push(new LeafletStop(stop));
        });

        return stops;
    }
    getWithSchedule(route: Route, isReverse: boolean): ScheduledStop[] {
        let stops: ScheduledStop[] = [];
        let stopSchedules = this.scheduleCollector.get(route, isReverse);

        stopSchedules.forEach(ss => {
            if (!this._stops.has(ss.stopId))
                return;
            let _stop = this._stops.get(ss.stopId);
            var stop = new ScheduledStop(_stop);
            stop.arrivalMoment = ss.arrivalTime;
            console.log('getWithSchedule', stop.name, stop.arrivalMoment)

            stops.push(stop);
        });

        if (isReverse)
            return stops;
        return stops.reverse();
    }

    select(stopId: number): void;
    select(stopId: number, selectionType: StopSelectionType): void;
    select(route: Route, isReverse: boolean): void;
    select(stopIds: number[], selectionType: StopSelectionType): void;
    select(...objects: any[]) {
        if (typeof (objects[0]) === 'number') {
            let stopId = objects[0];
            if (objects.length === 1) {
                this.selectStop(stopId);
            }
            else {
                let stopSelectionType = objects[1];
                if (stopSelectionType === StopSelectionType.Start) {
                    this.stops
                        .filter(s => s.selectionType === stopSelectionType)
                        .forEach(s => {
                            s.unselectDeep();
                        });
                    this.selectStopAsStart(stopId);
                } else if (stopSelectionType === StopSelectionType.End) {
                    this.stops
                        .filter(s => s.selectionType === stopSelectionType)
                        .forEach(s => {
                            s.unselectDeep();
                        });

                    this.selectStopAsFinish(stopId);
                }
            }
        }
        else if (objects[0] instanceof Route)
            this.selectByRoute(objects[0], objects[1]);
        else if (objects[0] instanceof Array) {
            let stopIds = objects[0];
            let selectionType = objects[1] as StopSelectionType;
            this.selectSegment(stopIds, selectionType);
        }
    }
    selectGently(stopId: number, selectionType?: StopSelectionType) {
        if (!this._stops.has(stopId))
            return;

        let stop = this._stops.get(stopId);
        if (!selectionType) {
            stop.select();
        } else if (selectionType === StopSelectionType.Start) {
            this.stops
                .filter(s => s.selectionType === selectionType)
                .forEach(s => {
                    s.unselectDeep();
                });
            this.selectStopAsStart(stopId);
        } else if (selectionType === StopSelectionType.End) {
            this.stops
                .filter(s => s.selectionType === selectionType)
                .forEach(s => {
                    s.unselectDeep();
                });

            this.selectStopAsFinish(stopId);
        }
    }

    unselect(isDeeply?: boolean) {
        this.stops.forEach(s => {
            if (s.isSelected) {
                if (isDeeply)
                    this.unselectDeepInternal(s);
                else
                    this.unselectInternal(s);
            }
        });
    }

    highlight(stopId: number) {
        if (!this._stops.has(stopId))
            return;

        let stop = this._stops.get(stopId);
        stop.highlight();
    }
    unhighlight(stopId: number) {
        if (!this._stops.has(stopId))
            return;

        let stop = this._stops.get(stopId);
        stop.unhighlight();
    }

    enable(): void;
    enable(stopId: number): void;
    enable(ids: number[]): void;
    enable(...objects: any[]) {
        if (objects.length === 0) {
            this._stops.forEach(s => s.enable());
        }
        else if (typeof (objects[0]) === 'number') {
            if (!this._stops.has(objects[0]))
                return;

            let stop = this._stops.get(objects[0]);
            stop.enable();
        } else if (objects[0] instanceof Array) {
            objects[0].forEach(id => {
                if (!this._stops.has(id))
                    return;

                let stop = this._stops.get(id);
                stop.enable();
            });
        }
    }

    disable(): void;
    disable(stopId: number): void;
    disable(ids: number[]): void;
    disable(...objects: any[]) {
        if (objects.length === 0) {
            this._stops.forEach(s => s.disable());
        }
        else if (typeof (objects[0]) === 'number') {
            if (!this._stops.has(objects[0]))
                return;

            let stop = this._stops.get(objects[0]);
            if (stop.isSelected)
                this.unselectInternal(stop);
            stop.disable();
        } else if (objects[0] instanceof Array) {
            objects[0].forEach(id => {
                if (!this._stops.has(id))
                    return;

                let stop = this._stops.get(id);
                if (stop.isSelected)
                    this.unselectInternal(stop);
                stop.disable();
            })
        }
    }

    search(substring: string): Observable<LeafletStop[]> {
        if (!substring) {
            return Observable.of<LeafletStop[]>([]);
        }
        let suggestedStops = this.stops.filter(s => s.name.toLowerCase().indexOf(substring.toLowerCase()) >= 0);

        let mappedStops = new Map<string, LeafletStop>();
        suggestedStops = suggestedStops.filter(s => {
            if (mappedStops.has(s.name)) {
                return false;
            }

            mappedStops.set(s.name, s);
            return true;
        });

        if (suggestedStops.length > 8) {
            suggestedStops = suggestedStops.slice(0, 8);
        }

        let observedStops = Observable.of<LeafletStop[]>(suggestedStops);
        return observedStops;
    }

    private convertInternal(s: Stop, canSelect: boolean): LeafletStop {
        // let coordinate = L.latLng(s.coordinate.latitude, s.coordinate.longitude);
        // let circleBackground = L.circleMarker(coordinate, this.optionsBackground);
        // let circle = L.circleMarker(coordinate, this.options);

        let lStop = new LeafletStop(s);
        // lStop.circleBackground = circleBackground;
        // lStop.circle = circle;
        this._stops.set(lStop.id, lStop);

        lStop.circle.bindTooltip(s.name);
        if (canSelect)
            lStop.circle.on('click', e => this.selectInternal(e, lStop));

        return lStop;
    }

    private selectStop(stopId: number) {
        if (!this._stops.has(stopId))
            return;

        let stop = this._stops.get(stopId);
        this.selectInternal(null, stop);
    }
    private selectStopAsStart(stopId: number) {
        if (!this._stops.has(stopId))
            return;

        let stop = this._stops.get(stopId);
        stop.selectAsStart();
    }
    private selectStopAsFinish(stopId: number) {
        if (!this._stops.has(stopId))
            return;

        let stop = this._stops.get(stopId);
        stop.selectAsFinish();
    }
    private selectStopAsGoBetween(stopId: number) {
        if (!this._stops.has(stopId))
            return;

        let stop = this._stops.get(stopId);
        stop.selectAsGoBetween();
    }
    private selectByRoute(route: Route, isReverse: boolean) {
        let ids = isReverse
            ? route.reverseStopIds
            : route.stopIds;

        ids.forEach(id => {
            if (!this._stops.has(id))
                return;

            let stop = this._stops.get(id);
            stop.select();
        });
    }
    private selectSegment(stopIds: number[], selectionType: StopSelectionType) {
        stopIds.forEach((id, index) => {
            let isFirst = index === 0;
            let isLast = index === stopIds.length - 1;
            switch (selectionType) {
                case StopSelectionType.Start:
                    if (isFirst) {
                        this.selectStopAsStart(id);
                    } else if (isLast) {
                        this.selectStopAsGoBetween(id);
                    } else {
                        this.selectGently(id);
                    }
                    break;
                case StopSelectionType.End:
                    if (isFirst) {
                        this.selectStopAsGoBetween(id);
                    } else if (isLast) {
                        this.selectStopAsFinish(id);
                    } else {
                        this.selectGently(id);
                    }
                    break;
                case StopSelectionType.StartEnd:
                    if (isFirst) {
                        this.selectStopAsStart(id);
                    } else if (isLast) {
                        this.selectStopAsFinish(id);
                    } else {
                        this.selectGently(id);
                    }
                    break;
                case StopSelectionType.GoBetween:
                    if (isFirst || isLast) {
                        this.selectStopAsGoBetween(id);
                    } else {
                        this.selectGently(id);
                    }
                    break;
                default:
                    this.selectGently(id);
            }
        });
    }

    private selectInternal(e: any, stop: LeafletStop) {
        if (this._onBeforeSelect)
            this._onBeforeSelect.trigger(stop);

        stop.select();

        if (this._onAfterSelect)
            this._onAfterSelect.trigger(stop);
    }
    private unselectInternal(stop: LeafletStop) {
        if (this._onBeforeUnselect)
            this._onBeforeUnselect.trigger(stop);

        stop.unselect();

        if (this._onAfterUnselect)
            this._onAfterUnselect.trigger(stop);
    }

    private unselectDeepInternal(stop: LeafletStop) {
        if (this._onBeforeUnselect)
            this._onBeforeUnselect.trigger(stop);

        stop.unselectDeep();

        if (this._onAfterUnselect)
            this._onAfterUnselect.trigger(stop);
    }
}


// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/common/stop-collector.ts